import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { SharedHeader } from "@/components/SharedHeader";

// Section Components
import HowItWorksSection from "@/components/sections/HowItWorksSection";
import HostGameSection from "@/components/sections/HostGameSection";
import FindGamesSection from "@/components/sections/FindGamesSection";
import VenuePartnershipsSection from "@/components/sections/VenuePartnershipsSection";
import FeaturedGamesSection from "@/components/sections/FeaturedGamesSection";
import MembershipBenefitsSection from "@/components/sections/MembershipBenefitsSection";

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const [activeSection, setActiveSection] = useState(null); // null = show hero
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [venues, setVenues] = useState([]);
  const [games, setGames] = useState([]);

  useEffect(() => {
    fetchData();
    // Check if navigated with a section state
    if (location.state?.section) {
      setActiveSection(location.state.section);
    }
  }, [location.state]);

  const fetchData = async () => {
    try {
      // Seed data first
      await axios.post(`${API}/seed/venues`);
      await axios.post(`${API}/seed/events`);

      // Fetch data
      const [eventsRes, venuesRes, gamesRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/venues`),
        axios.get(`${API}/games/featured`),
      ]);

      setEvents(eventsRes.data.events);
      setVenues(venuesRes.data.venues);
      setGames(gamesRes.data.games);
    } catch (err) {
      console.error("Failed to fetch data:", err);
    }
  };

  const handleRSVP = async (eventId) => {
    try {
      await axios.post(`${API}/events/rsvp`, { event_id: eventId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Successfully registered for event!");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to register");
    }
  };

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="home-page">
      {/* Shared Header */}
      <SharedHeader 
        activeSection={activeSection}
        setActiveSection={handleSectionChange}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      {/* Main Content */}
      <main className="pt-16">
        {/* Hero Section - Only show when no section is active */}
        {!activeSection && (
          <>
            <section className="relative py-20 md:py-32 px-4 overflow-hidden">
              <div 
                className="absolute inset-0 opacity-30"
                style={{
                  background: "radial-gradient(ellipse at 30% 50%, rgba(122, 98, 0, 0.3) 0%, transparent 50%)"
                }}
              />
              
              <div className="max-w-7xl mx-auto relative z-10">
                <div className="max-w-3xl">
                  <h1 className="font-bebas text-5xl md:text-7xl lg:text-8xl tracking-wider text-white leading-none">
                    WELCOME TO
                    <span className="block gold-text">BOARD2BOSTON™</span>
                  </h1>
                  <p className="mt-6 text-[#A1A1AA] font-ubuntu max-w-xl whitespace-nowrap">
                    <span className="block text-2xl md:text-3xl">Welcome to the Future</span>
                    <span className="block text-lg md:text-xl">of Competitive Gaming and Social Connection.</span>
                  </p>
                  <div className="mt-8 flex flex-wrap gap-4">
                    <Button 
                      className="btn-gold px-8 py-6 text-lg"
                      onClick={() => setActiveSection("find-games")}
                      data-testid="hero-find-games"
                    >
                      FIND GAMES
                    </Button>
                    <Button 
                      className="btn-outline-gold px-8 py-6 text-lg"
                      onClick={() => setActiveSection("host-game")}
                      data-testid="hero-host-game"
                    >
                      HOST A GAME
                    </Button>
                  </div>
                </div>
              </div>
            </section>

            {/* Quick Stats */}
            <section className="py-8 border-y border-white/10 bg-[#0A0A0A]">
              <div className="max-w-7xl mx-auto px-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <p className="font-bebas text-4xl text-[#7a6200]">{events.length}+</p>
                    <p className="text-[#A1A1AA] font-ubuntu text-sm">Upcoming Events</p>
                  </div>
                  <div className="text-center">
                    <p className="font-bebas text-4xl text-[#7a6200]">{venues.length}+</p>
                    <p className="text-[#A1A1AA] font-ubuntu text-sm">Partner Venues</p>
                  </div>
                  <div className="text-center">
                    <p className="font-bebas text-4xl text-[#7a6200]">100+</p>
                    <p className="text-[#A1A1AA] font-ubuntu text-sm">Active Players</p>
                  </div>
                  <div className="text-center">
                    <p className="font-bebas text-4xl text-[#7a6200]">{games.length}+</p>
                    <p className="text-[#A1A1AA] font-ubuntu text-sm">Game Types</p>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}

        {/* Section Content */}
        {activeSection && (
          <section className="py-12 md:py-20 px-4">
            <div className="max-w-7xl mx-auto">
              {activeSection === "how-it-works" && <HowItWorksSection />}
              {activeSection === "host-game" && <HostGameSection token={token} onSuccess={fetchData} />}
              {activeSection === "find-games" && <FindGamesSection events={events} onRSVP={handleRSVP} />}
              {activeSection === "venues" && <VenuePartnershipsSection venues={venues} />}
              {activeSection === "featured" && <FeaturedGamesSection games={games} />}
              {activeSection === "membership" && <MembershipBenefitsSection />}
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-[#0A0A0A] py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_hoop-invite/artifacts/r8lcaocz_Board2Boston%20favicon.png"
                alt="Board2Boston"
                className="mb-4"
                style={{ height: '193px' }}
              />
              <p className="text-[#52525B] font-ubuntu text-sm">
                Premier Gaming in Your Community
              </p>
            </div>
            <div>
              <h4 className="font-bebas text-lg tracking-wider text-white mb-4">RESOURCES</h4>
              <ul className="space-y-2 text-[#A1A1AA] font-ubuntu text-sm">
                <li><a href="/rules" className="hover:text-[#7a6200] transition-colors">Game Rules</a></li>
                <li><a href="/apps" className="hover:text-[#7a6200] transition-colors">Mobile Apps</a></li>
                <li><a href="/sponsors" className="hover:text-[#7a6200] transition-colors">Sponsors</a></li>
                <li><a href="/contact" className="hover:text-[#7a6200] transition-colors">Contact Us</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bebas text-lg tracking-wider text-white mb-4">COMMUNITY</h4>
              <ul className="space-y-2 text-[#A1A1AA] font-ubuntu text-sm">
                <li>Events</li>
                <li>Tournaments</li>
                <li>Venues</li>
                <li>Membership</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bebas text-lg tracking-wider text-white mb-4">LEGAL</h4>
              <ul className="space-y-2 text-[#A1A1AA] font-ubuntu text-sm">
                <li><a href="/terms" className="hover:text-[#7a6200] transition-colors">Terms of Service</a></li>
                <li><a href="/privacy" className="hover:text-[#7a6200] transition-colors">Privacy Policy</a></li>
                <li><a href="/mission" className="hover:text-[#7a6200] transition-colors">Our Mission</a></li>
                <li><a href="/about" className="hover:text-[#7a6200] transition-colors">About Us</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-white/10 text-center">
            <p className="text-[#52525B] font-ubuntu text-sm">
              © 2026 All Rights Reserved | Board2Boston™ | 713 Consulting and Development
            </p>
            <p className="text-[#52525B] font-ubuntu text-xs mt-2">
              1942 W. Gray St., Box #1259 | Houston, TX 77019 | United States
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
